import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authAPI } from '../api/auth';

// After this many CONSECUTIVE ECONNABORTED (timed-out) auth-check failures in a
// row, force a logout instead of leaving isAuthenticated/user untouched
// indefinitely -- a single named constant so the threshold is easy to tune.
const MAX_CONSECUTIVE_TIMEOUT_FAILURES = 3;

// /api/auth/me payload. lifetime_discount_percent is read-only here (admin-set).
// The index signature keeps the previous `any` flexibility for other fields.
export interface AuthUser {
  lifetime_discount_percent?: number;
  [key: string]: any;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Bump-then-capture-then-compare session guard (same pattern as
  // AdminQBMenu.tsx/AdminCategories.tsx/AdminInstantCakes.tsx/
  // AdminBubbleTeaOptions.tsx's sessionIdRef): lets a stale/superseded
  // checkAuth() call detect, after its await resolves, that a newer call has
  // since started -- and skip mutating state with its now-outdated result.
  // IMPORTANT: this must be bumped by EVERY code path that clears/supersedes
  // isAuthenticated/user, not just inside checkAuth() itself -- see the
  // handleStorageChange 'access_token' removal branch and the monkey-patched
  // localStorage.removeItem override below, both of which also bump it.
  // REENTRANCY HAZARD: checkAuth()'s own forced-logout branches call
  // authAPI.logout(), whose real implementation calls localStorage.removeItem
  // ('access_token') -- which reenters the override below and bumps this ref
  // again, so by the time those branches' shared `finally` block runs, their
  // own sessionId no longer matches and setIsLoading(false) there is skipped;
  // those branches must set isLoading directly instead of relying on finally.
  const checkAuthSessionIdRef = useRef(0);
  // Counts CONSECUTIVE ECONNABORTED failures across checkAuth() invocations;
  // reset to 0 on the no-token branch, a successful getCurrentUser(), any
  // non-ECONNABORTED failure (which already logs out via its own path below),
  // login(), a storage event that adds/changes access_token, and logout paths.
  const consecutiveTimeoutFailuresRef = useRef(0);

  const checkAuth = async () => {
    // Bump once at function entry (uniform rule, even though the synchronous
    // !token branch below doesn't strictly need the guard).
    checkAuthSessionIdRef.current += 1;
    const sessionId = checkAuthSessionIdRef.current;

    const token = authAPI.getAccessToken();

    if (!token) {
      consecutiveTimeoutFailuresRef.current = 0;
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('user');
      setIsLoading(false);
      return;
    }

    try {
      // Try to fetch current user details from API
      const userData = await authAPI.getCurrentUser();
      if (sessionId === checkAuthSessionIdRef.current) {
        consecutiveTimeoutFailuresRef.current = 0;
        setUser(userData);
        setIsAuthenticated(true);
        // Don't store user data in localStorage for security reasons
        // User data should only come from authenticated API calls
      }
    } catch (error: any) {
      console.error('Auth check failed:', error);
      // A timed-out auth-check call (slow-but-alive network) is not the same as a
      // genuinely invalid/expired session -- don't log the user out over it, unless
      // it happens MAX_CONSECUTIVE_TIMEOUT_FAILURES times in a row, in which case we
      // assume the session is unrecoverable and force a logout. Mirrors the same
      // ECONNABORTED guard in axios.ts's token-refresh catch block.
      if (error?.code !== 'ECONNABORTED') {
        if (sessionId === checkAuthSessionIdRef.current) {
          consecutiveTimeoutFailuresRef.current = 0;
          // For any other auth check failure, clear authentication
          // This ensures users can't access protected pages with expired/invalid tokens
          authAPI.logout();
          setIsAuthenticated(false);
          setUser(null);
          localStorage.removeItem('user');
          // authAPI.logout() called above reenters the checkAuthSessionIdRef-bumping
          // localStorage.removeItem override, so this branch's own sessionId no
          // longer matches by the time the shared finally block runs -- set
          // isLoading directly here instead of relying on it.
          setIsLoading(false);
        }
      } else if (sessionId === checkAuthSessionIdRef.current) {
        consecutiveTimeoutFailuresRef.current += 1;
        if (consecutiveTimeoutFailuresRef.current >= MAX_CONSECUTIVE_TIMEOUT_FAILURES) {
          consecutiveTimeoutFailuresRef.current = 0;
          authAPI.logout();
          setIsAuthenticated(false);
          setUser(null);
          localStorage.removeItem('user');
          // authAPI.logout() called above reenters the checkAuthSessionIdRef-bumping
          // localStorage.removeItem override, so this branch's own sessionId no
          // longer matches by the time the shared finally block runs -- set
          // isLoading directly here instead of relying on it.
          setIsLoading(false);
        }
        // Otherwise, leave isAuthenticated/user untouched (existing behavior).
      }
    } finally {
      if (sessionId === checkAuthSessionIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    // Check authentication status on mount
    checkAuth();

    // Listen for storage changes (e.g., when tokens are removed manually)
    const handleStorageChange = (e: StorageEvent) => {
      // If access_token was removed, immediately clear authentication
      if (e.key === 'access_token' && e.newValue === null) {
        consecutiveTimeoutFailuresRef.current = 0;
        checkAuthSessionIdRef.current += 1;
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem('user');
        // This bump can invalidate an in-flight checkAuth() call (e.g. the
        // mount-triggered one) before its own finally block runs, so its
        // setIsLoading(false) would be skipped by the sessionId guard -- set
        // isLoading directly here too, mirroring checkAuth's own
        // forced-logout branches above.
        setIsLoading(false);
      }
      // If access_token was added or changed, recheck auth
      else if (e.key === 'access_token' && e.newValue !== null) {
        consecutiveTimeoutFailuresRef.current = 0; // new/changed token: drop any stale timeout count
        checkAuth();
      }
    };

    // Also handle manual token removal in the same tab
    const originalRemoveItem = localStorage.removeItem;
    localStorage.removeItem = function(key: string) {
      originalRemoveItem.call(this, key);
      // If access_token was removed, immediately clear authentication
      if (key === 'access_token') {
        consecutiveTimeoutFailuresRef.current = 0;
        checkAuthSessionIdRef.current += 1;
        setIsAuthenticated(false);
        setUser(null);
        // Use the original function to avoid recursion
        originalRemoveItem.call(localStorage, 'user');
        // This bump can invalidate an in-flight checkAuth() call (e.g. the
        // mount-triggered one) before its own finally block runs, so its
        // setIsLoading(false) would be skipped by the sessionId guard -- set
        // isLoading directly here too, mirroring checkAuth's own
        // forced-logout branches above.
        setIsLoading(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      localStorage.removeItem = originalRemoveItem;
    };
  }, []);

  const login = async (email: string, password: string) => {
    await authAPI.login(email, password);
    consecutiveTimeoutFailuresRef.current = 0; // new session: drop any stale pre-login timeout count
    await checkAuth(); // Update auth state after successful login
  };

  const logout = () => {
    authAPI.logout();
    setIsAuthenticated(false);
    setUser(null);
    consecutiveTimeoutFailuresRef.current = 0;
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, checkAuth }}>
      {!isLoading ? children : <div>Loading...</div>}
    </AuthContext.Provider>
  );
};